import { useFrame } from '@react-three/fiber';
import { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';

// Simple R3F canvas with looping random explosions
// Single-file React component. Drop into a React app with @react-three/fiber installed.

const Explosions = () => {
	// configuration
	const maxExplosions = 10;
	const particlesPerExplosion = 120;
	const totalParticles = maxExplosions * particlesPerExplosion;
	const explosionDuration = 1.8; // seconds

	// Refs for buffer attributes
	const pointsRef = useRef();
	const positions = useMemo(
		() => new Float32Array(totalParticles * 3),
		[totalParticles]
	);
	const velocities = useMemo(
		() => new Float32Array(totalParticles * 3),
		[totalParticles]
	);
	const startTimes = useMemo(
		() => new Float32Array(totalParticles),
		[totalParticles]
	);
	const colors = useMemo(
		() => new Float32Array(totalParticles * 3),
		[totalParticles]
	);
	const sizes = useMemo(
		() => new Float32Array(totalParticles),
		[totalParticles]
	);

	// Keep track of which explosion slot to reuse next
	const nextExplosionIndex = useRef(0);
	const clock = useRef({ elapsed: 0 });

	// initialize all particles to off-screen and zeroed
	useEffect(() => {
		for (let i = 0; i < totalParticles; i++) {
			const pi3 = i * 3;
			positions[pi3] = 9999; // far away
			positions[pi3 + 1] = 9999;
			positions[pi3 + 2] = 9999;
			velocities[pi3] = 0;
			velocities[pi3 + 1] = 0;
			velocities[pi3 + 2] = 0;
			startTimes[i] = -9999;
			colors[pi3] = 1.0;
			colors[pi3 + 1] = 0.6;
			colors[pi3 + 2] = 0.1;
			sizes[i] = 6.0;
		}
	}, [positions, velocities, startTimes, colors, sizes, totalParticles]);

	// spawn an explosion into slot 'slot'
	function spawnExplosion(slot, timeNow) {
		const centerX = (Math.random() - 0.5) * 20;
		const centerY = (Math.random() - 0.5) * 12;
		const centerZ = (Math.random() - 0.5) * 8;
		const base = slot * particlesPerExplosion;

		// pick a warm color for the explosion
		const hue = Math.random() * 0.12 + 0.05; // orange-ish
		const color = new THREE.Color().setHSL(hue, 1.0, 0.5);

		for (let i = 0; i < particlesPerExplosion; i++) {
			const idx = base + i;
			const p3 = idx * 3;

			// start at center
			positions[p3] = centerX;
			positions[p3 + 1] = centerY;
			positions[p3 + 2] = centerZ;

			// random velocity outward
			const speed = 6 + Math.random() * 12;
			// random direction on sphere with slight upward bias
			const theta = Math.acos(2 * Math.random() - 1);
			const phi = Math.random() * Math.PI * 2;
			const vx = Math.sin(theta) * Math.cos(phi);
			const vy = Math.cos(theta) * 1.2; // upward bias
			const vz = Math.sin(theta) * Math.sin(phi);

			velocities[p3] = vx * speed;
			velocities[p3 + 1] = vy * speed;
			velocities[p3 + 2] = vz * speed;

			// color variations
			colors[p3] = color.r + (Math.random() - 0.5) * 0.15;
			colors[p3 + 1] = color.g + (Math.random() - 0.5) * 0.15;
			colors[p3 + 2] = color.b + (Math.random() - 0.5) * 0.15;

			sizes[idx] = 6 + Math.random() * 6;

			startTimes[idx] = timeNow;
		}
	}

	// spawn periodically
	useEffect(() => {
		const interval = setInterval(
			() => {
				const slot = nextExplosionIndex.current % maxExplosions;
				spawnExplosion(slot, clock.current.elapsed);
				nextExplosionIndex.current += 1;
			},
			500 + Math.random() * 1200
		); // variant interval
		return () => clearInterval(interval);
	}, []);

	useFrame((state, delta) => {
		clock.current.elapsed += delta;
		const t = clock.current.elapsed;

		// update particles
		const gravity = -9.8 * 0.6; // mild gravity

		for (let i = 0; i < totalParticles; i++) {
			const p3 = i * 3;
			const st = startTimes[i];
			const age = t - st;

			if (age > 0 && age < explosionDuration) {
				// particle is alive, integrate
				velocities[p3 + 1] += gravity * delta;
				positions[p3] += velocities[p3] * delta;
				positions[p3 + 1] += velocities[p3 + 1] * delta;
				positions[p3 + 2] += velocities[p3 + 2] * delta;

				// fade out by moving color towards black and shrinking size
				const lifeRatio = 1 - age / explosionDuration;
				sizes[i] = 2 + 10 * lifeRatio;
			} else if (age >= explosionDuration) {
				// move off-screen to avoid lingering
				positions[p3] = 9999;
				positions[p3 + 1] = 9999;
				positions[p3 + 2] = 9999;
				velocities[p3] = velocities[p3 + 1] = velocities[p3 + 2] = 0;
				startTimes[i] = -9999;
			}
		}

		// push updates to GPU
		if (pointsRef.current) {
			const geom = pointsRef.current.geometry;
			geom.attributes.position.needsUpdate = true;
			geom.attributes.customColor.needsUpdate = true;
			geom.attributes.size.needsUpdate = true;
		}
	});

	// build the BufferGeometry once
	const geometry = useMemo(() => {
		const geom = new THREE.BufferGeometry();
		geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
		geom.setAttribute('customColor', new THREE.BufferAttribute(colors, 3));
		geom.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
		return geom;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// custom shader material to allow per-point size and color
	const material = useMemo(() => {
		const mat = new THREE.ShaderMaterial({
			transparent: true,
			depthWrite: false,
			blending: THREE.AdditiveBlending,
			uniforms: {
				pointTexture: { value: null },
			},
			vertexShader: `
        attribute float size;
        attribute vec3 customColor;
        varying vec3 vColor;
        void main() {
          vColor = customColor;
          vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
          gl_PointSize = size * ( 300.0 / -mvPosition.z );
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
			fragmentShader: `
        varying vec3 vColor;
        void main() {
          float dist = length(gl_PointCoord - vec2(0.5));
          float alpha = 1.0 - smoothstep(0.45, 0.6, dist);
          gl_FragColor = vec4(vColor, alpha);
        }
      `,
		});
		return mat;
	}, []);

	return (
		<points
			ref={pointsRef}
			geometry={geometry}
			material={material}
			renderOrder={10}
		>
			{/* no children */}
		</points>
	);
};

export default Explosions;
